<script setup lang="ts">
const props = withDefaults(defineProps<{
  size?: 'x-small' | 'small' | 'default' | 'large' | 'x-large' | number | string
  color?: string
  image?: string
  icon?: string
  rounded?: boolean | string
}>(), {
  size: 'default',
})

const sizeMap: Record<string, string> = {
  'x-small': '24px',
  'small': '32px',
  'default': '40px',
  'large': '48px',
  'x-large': '56px',
}

const computedSize = computed(() => {
  if (typeof props.size === 'number') return `${props.size}px`
  if (typeof props.size === 'string' && /^\d+$/.test(props.size)) return `${props.size}px`
  return sizeMap[props.size as string] || sizeMap.default
})

const roundedClass = computed(() => {
  if (!props.rounded) return ''
  if (props.rounded === true || props.rounded === 'full' || props.rounded === 'circle') return ''  // default is circle
  return 'm-avatar--rounded'  // any string value = rounded corners
})

const colorMap: Record<string, string> = {
  primary: 'var(--m-primary)',
  secondary: 'var(--m-secondary)',
  accent: 'var(--m-accent)',
  error: 'var(--m-error)',
  warning: 'var(--m-warning)',
  info: 'var(--m-info)',
  success: 'var(--m-success)',
  'surface-variant': 'var(--m-surface-variant)',
}
</script>

<template>
  <div
    class="m-avatar"
    :class="roundedClass"
    :style="{
      width: computedSize,
      height: computedSize,
      backgroundColor: color ? (colorMap[color] || color) : undefined,
    }"
  >
    <img v-if="image" :src="image" class="m-avatar__img" alt="" />
    <MIcon v-else-if="icon" :icon="icon" :size="typeof size === 'number' ? Math.floor(size * 0.6) : size === 'x-small' ? 'x-small' : 'small'" />
    <slot v-else />
  </div>
</template>

<style scoped>
.m-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--m-radius-full);
  overflow: hidden;
  flex-shrink: 0;
  font-weight: 500;
  color: var(--m-on-primary);
  font-size: var(--m-font-size-sm);
}

.m-avatar--rounded { border-radius: var(--m-radius); }

.m-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
